import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {Shield, Key, Mail, Calendar, CheckCircle, XCircle, AlertCircle, Lock, MonitorSmartphone, TrendingUp} from 'lucide-react';
import toast from 'react-hot-toast';
import { lumi } from '../lib/lumi';

interface AccessCode {
  _id: string;
  code: string;
  email: string;
  planId: string;
  status: 'active' | 'revoked';
  createdAt: string;
}

const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'revoked'>('all');
  const [installStats, setInstallStats] = useState<{date: string, count: number}[]>([]);

  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (adminAuth === 'TitanGryx2024') {
      setIsAuthenticated(true);
      fetchCodes();
      fetchInstallStats();
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'TitanGryx2024') {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'TitanGryx2024');
      toast.success('Accès admin activé');
      fetchCodes();
    } else {
      toast.error('Mot de passe incorrect');
    }
  };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const response = await lumi.entities.access_codes.list({
        sort: { createdAt: -1 },
        limit: 100
      });
      setCodes(response.list);
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast.error('Erreur lors du chargement des codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallStats = async () => {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const response = await lumi.entities.user_events.list({
        filter: {
          eventType: 'app_install',
          timestamp: { $gte: thirtyDaysAgo.toISOString() }
        },
        sort: { timestamp: 1 },
        limit: 1000
      });

      // Grouper par jour
      const statsMap = new Map<string, number>();
      const today = new Date();
      
      // Initialiser tous les jours à 0
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        statsMap.set(dateStr, 0);
      }

      // Compter les installations par jour
      response.list.forEach((event: any) => {
        const dateStr = event.timestamp.split('T')[0];
        if (statsMap.has(dateStr)) {
          statsMap.set(dateStr, (statsMap.get(dateStr) || 0) + 1);
        }
      });

      const stats = Array.from(statsMap.entries()).map(([date, count]) => ({
        date,
        count
      }));

      setInstallStats(stats);
    } catch (error) {
      console.error('Error fetching install stats:', error);
    }
  };

  const revokeCode = async (codeId: string) => {
    try {
      await lumi.entities.access_codes.update(codeId, { status: 'revoked' });
      toast.success('Code révoqué');
      fetchCodes();
    } catch (error) {
      console.error('Error revoking code:', error);
      toast.error('Erreur lors de la révocation');
    }
  };

  const reactivateCode = async (codeId: string) => {
    try {
      await lumi.entities.access_codes.update(codeId, { status: 'active' });
      toast.success('Code réactivé');
      fetchCodes();
    } catch (error) {
      console.error('Error reactivating code:', error);
      toast.error('Erreur lors de la réactivation');
    }
  };

  const filteredCodes = codes.filter(code => {
    if (filter === 'all') return true;
    return code.status === filter;
  });

  const stats = {
    total: codes.length,
    active: codes.filter(c => c.status === 'active').length,
    revoked: codes.filter(c => c.status === 'revoked').length
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 p-8 rounded-xl border-2 border-red-600 max-w-md w-full"
        >
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-red-600 to-green-600 p-4 rounded-full">
              <Shield className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-6">
            Admin - Gestion des Codes
          </h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2 font-medium">
                <Lock className="inline w-4 h-4 mr-2" />
                Mot de passe admin
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-red-500 focus:outline-none"
                placeholder="TitanGryx2024"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-red-600 to-green-600 text-white py-3 rounded-lg font-bold hover:shadow-lg transition-all"
            >
              Se connecter
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-r from-red-600 to-green-600 p-3 rounded-xl">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Gestion Admin</h1>
                <p className="text-gray-400">Codes d'accès clients</p>
              </div>
            </div>
            <button
              onClick={() => {
                fetchCodes();
                fetchInstallStats();
              }}
              className="bg-gray-700 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-all"
            >
              Actualiser
            </button>
          </div>

          {/* Graphique Installations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 p-6 rounded-xl border border-gray-700 mb-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-blue-500" />
              <h3 className="text-xl font-bold text-white">Installations - 30 derniers jours</h3>
            </div>
            <div className="flex items-end justify-between gap-1 h-48">
              {installStats.map((stat, index) => {
                const maxCount = Math.max(...installStats.map(s => s.count), 1);
                const heightPercent = (stat.count / maxCount) * 100;
                const isRecent = index >= installStats.length - 7;
                
                return (
                  <div key={stat.date} className="flex-1 flex flex-col items-center group relative">
                    <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      {new Date(stat.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}: {stat.count}
                    </div>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${heightPercent}%` }}
                      transition={{ delay: index * 0.02 }}
                      className={`w-full rounded-t transition-all ${
                        stat.count > 0
                          ? isRecent
                            ? 'bg-gradient-to-t from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300'
                            : 'bg-gradient-to-t from-gray-600 to-gray-500 hover:from-gray-500 hover:to-gray-400'
                          : 'bg-gray-700'
                      }`}
                      style={{ minHeight: stat.count > 0 ? '4px' : '2px' }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>{installStats[0]?.date && new Date(installStats[0].date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
              <span>Aujourd'hui</span>
            </div>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-400">7 derniers jours</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-500 rounded"></div>
                <span className="text-gray-400">Période antérieure</span>
              </div>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total</p>
                  <p className="text-3xl font-bold text-white">{stats.total}</p>
                </div>
                <Key className="w-8 h-8 text-gray-500" />
              </div>
            </div>
            <div className="bg-gray-800 p-4 rounded-xl border border-green-600">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Actifs</p>
                  <p className="text-3xl font-bold text-green-500">{stats.active}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-gray-800 p-4 rounded-xl border border-red-600">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Révoqués</p>
                  <p className="text-3xl font-bold text-red-500">{stats.revoked}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-red-600 to-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Tous ({stats.total})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Actifs ({stats.active})
            </button>
            <button
              onClick={() => setFilter('revoked')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'revoked'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Révoqués ({stats.revoked})
            </button>
          </div>
        </motion.div>

        {/* Codes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-gray-400 mt-4">Chargement...</p>
          </div>
        ) : filteredCodes.length === 0 ? (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aucun code trouvé</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCodes.map((code) => (
              <motion.div
                key={code._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`bg-gray-800 p-6 rounded-xl border-2 ${
                  code.status === 'active' ? 'border-green-600' : 'border-red-600'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                        code.status === 'active'
                          ? 'bg-green-600 text-white'
                          : 'bg-red-600 text-white'
                      }`}>
                        {code.status === 'active' ? 'ACTIF' : 'RÉVOQUÉ'}
                      </div>
                      <span className="bg-gray-700 px-4 py-1 rounded-lg font-mono text-white text-lg font-bold">
                        {code.code}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>{code.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Key className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">
                          {code.planId === 'premium_app_monthly' ? '6,99€/mois' : '49€/an'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>{new Date(code.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {code.status === 'active' ? (
                      <button
                        onClick={() => revokeCode(code._id)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Révoquer
                      </button>
                    ) : (
                      <button
                        onClick={() => reactivateCode(code._id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Réactiver
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;